/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Dispatch } from 'redux';
import dayjs from 'dayjs';
import { AccountingEventType } from '@received/pricing-model';
import { NavigateFunction } from 'react-router-dom';
import { PathsConfig, httpService } from '../services';
import { setOpenSuccessErrorModal } from '../storeSlices/errorSuccessSlice';
import { downloadFile } from './GeneralUtils';
import { setShowHideToastNotification, setShowImportedDataIndicator } from '../storeSlices/generalSlice';
import { ToastNotificationComponentType } from '../components/NotificationCard/NotificationCard.utils';
import { Invoice, InvoiceView } from '../types/invoiceTypes';
import { CreditNoteStatusTag, InvoiceStatusTag, MenuOption, PayoutStatusTag } from '../components';
import { CreditNote, CreditNoteView } from '../types/creditNoteTypes';
import { IntegrationType } from '../types/integrationTypes';
import { colors } from '../constants/colors';
import { successErrorMassageOptions } from '../components/SuccessErrorModal/SuccessErrorModal.utils';
import { isEmbedded } from '../services/embedded/embeddedService';
import { DATE_FORMAT_FOR_SERVER } from '../constants/templateConstants';
import { StatusTagProps } from '../components/StatusTags/StatusTags.utils';
import { ErrorSuccessModalTypes } from '../types/generalTypes';

// Embedded (cross-origin iframe) fallback for the SSE-driven PDF download.
//
// Standalone, the server signals "PDF ready" over SSE (PDF_GENERATION_COMPLETED),
// which triggers downloadDocumentPDFById. That event never reaches a cross-origin
// iframe — EventSource can't carry the Bearer token and the SSE channel is keyed
// on a cookie-session clientID that the iframe doesn't have. So in embedded mode
// we poll the Bearer-authed download endpoint instead: it returns 404 ("...try
// again") until the PDF lands in Redis, and only deletes the task once it streams
// the file, so re-polling the same taskId is safe.
const PDF_DOWNLOAD_POLL_INTERVAL_MS = 1_000;
const PDF_DOWNLOAD_POLL_MAX_ATTEMPTS = 60; // ~60s budget; generation is usually a few seconds

export const pollDownloadDocumentPdf = async (dispatch: Dispatch, taskId?: string, failureMessage?: string) => {
	if (!taskId) return;

	for (let attempt = 0; attempt < PDF_DOWNLOAD_POLL_MAX_ATTEMPTS; attempt++) {
		try {
			const res = await httpService({
				dispatch,
				path: PathsConfig.downloadDocumentById,
				urlParam: { generationId: taskId },
				responseType: 'blob',
				showLoader: false,
			});

			dispatch(
				setShowHideToastNotification({
					showToastNotification: true,
					toastNotificationComponent: ToastNotificationComponentType.GeneralNotification,
					title: 'fileBeingDownloaded',
					description: 'fileBeingDownloadedDescription',
					icon: 'downloadArrow',
					sideColor: colors.success,
				}),
			);

			downloadFile(res);
			return;
		} catch (error: any) {
			// 404 means the PDF isn't in Redis yet — keep polling. Anything else is terminal.
			if (error?.response?.status === 404) {
				await new Promise((resolve) => setTimeout(resolve, PDF_DOWNLOAD_POLL_INTERVAL_MS));
				continue;
			}
			dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: failureMessage }));
			return;
		}
	}

	// Polling budget exhausted. There's no error object here, so pass modalType explicitly —
	// otherwise the modal defaults to the SUCCESS variant ("Great!" + green check) with failure text.
	dispatch(setOpenSuccessErrorModal({ successErrorMassage: failureMessage, modalType: ErrorSuccessModalTypes.ERROR }));
};

// Kicks off server-side PDF generation for a document, then shows a "preparing" toast. In the embedded
// (iframe) flow the SSE "PDF ready" event never reaches the frame, so we poll for the generated PDF and
// download it; standalone relies on the SSE event. Shared by the invoice and credit-note download flows —
// they differ only in the generate path and the failure message.
export const startGenerateDocumentPDFById = async (
	dispatch: Dispatch,
	id: string | undefined,
	path: PathsConfig,
	failureMessage: string,
) => {
	if (!id) return;

	try {
		const res = await httpService({ dispatch, path, urlParam: { id } });

		dispatch(
			setShowHideToastNotification({
				showToastNotification: true,
				toastNotificationComponent: ToastNotificationComponentType.GeneralNotification,
				title: 'PreparingFileForDownload',
				description: 'PreparingFileForDownloadDescription',
				icon: 'clock',
			}),
		);

		if (isEmbedded()) {
			await pollDownloadDocumentPdf(dispatch, res?.data?.taskId, failureMessage);
		}
	} catch (error) {
		dispatch(setOpenSuccessErrorModal({ responseError: error }));
	}
};

export const downloadDocumentPDFById = async (dispatch: Dispatch, id?: string, count?: number) => {
	if (!id) return;

	try {
		const res = await httpService({
			dispatch,
			path: PathsConfig.downloadDocumentById,
			urlParam: { generationId: id },
			responseType: 'blob',
		});
		dispatch(
			setShowHideToastNotification({
				showToastNotification: true,
				toastNotificationComponent: ToastNotificationComponentType.GeneralNotification,
				title: count && count > 1 ? 'filesBeingDownloaded' : 'fileBeingDownloaded',
				description: count && count > 1 ? 'filesBeingDownloadedDescription' : 'fileBeingDownloadedDescription',
				icon: 'downloadArrow',
				sideColor: colors.success,
			}),
		);

		downloadFile(res);
	} catch (error) {
		dispatch(setOpenSuccessErrorModal({ responseError: error }));
	}
};

export interface DocumentMenuListProps {
	item: InvoiceView | Invoice | CreditNote | CreditNoteView;
	dispatch: Dispatch;
	navigate: NavigateFunction;
	t: any;
	availableIntegration?: IntegrationType[];
	editDocumentNumber?: MenuOption;
	onRefresh?: () => void;
	onPreview?: () => void;
}

export const sendMultipleDocuments = async (t: any, ids: string[], dispatch: Dispatch) => {
	try {
		httpService({
			dispatch,
			showLoader: false,
			path: PathsConfig.sendMultipleDocuments,
			data: { ids },
		});

		dispatch(
			setShowHideToastNotification({
				showToastNotification: true,
				title: 'sendingDocuments',
				description: 'theActionTakeAWhile',
			}),
		);

		return dispatch(
			setShowImportedDataIndicator({
				totalItems: ids.length,
				importedItems: 0,
				barTitle: t('sent'),
			}),
		);
	} catch (error) {
		dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.SEND_DOCUMENT }));
	}
};

export const submitDocuments = async (date: Date, documents: { id: string; type: AccountingEventType }[], dispatch: Dispatch) => {
	try {
		await httpService({
			dispatch,
			path: PathsConfig.submitDocument,
			data: { documents, submissionDate: dayjs(date).format(DATE_FORMAT_FOR_SERVER) },
		});
		dispatch(setOpenSuccessErrorModal({ successErrorMassage: successErrorMassageOptions.SUCCESS.DOCUMENT_UPDATED }));
	} catch (error) {
		dispatch(setOpenSuccessErrorModal({ responseError: error, successErrorMassage: successErrorMassageOptions.ERROR.FAILED_UPDATE_DOCUMENT }));
	}
};

export const reconcileDocuments = async (date: Date, documents: { id: string; type: AccountingEventType }[], dispatch: Dispatch) => {
	try {
		await httpService({
			dispatch,
			path: PathsConfig.reconcileDocuments,
			data: { documents, reconciliationDate: dayjs(date).format(DATE_FORMAT_FOR_SERVER) },
		});
		dispatch(setOpenSuccessErrorModal({ successErrorMassage: successErrorMassageOptions.SUCCESS.INVOICE_STATUS_UPDATED }));
	} catch (error) {
		dispatch(setOpenSuccessErrorModal({ responseError: error }));
	}
};

interface GenericStatusTagProps extends StatusTagProps {
	document: any;
}

export const getStatusTagByDocumentType = (props: GenericStatusTagProps) => {
	switch (props?.document?.accountingEventType) {
		case AccountingEventType.DEBIT:
		case AccountingEventType.INVOICE:
			return <InvoiceStatusTag {...props} />;
		case AccountingEventType.CREDIT:
			return <CreditNoteStatusTag {...props} />;
		case AccountingEventType.PAYOUT:
			return <PayoutStatusTag {...props} />;

		default:
			return <InvoiceStatusTag {...props} />;
	}
};

export interface DocumentViewMenuListProps {
	t: any;
	onDownloadCSV: (props: any) => void; // each 'onDownloadCSV' function will have different props for different pages
}

export const documentsViewOptionsList = ({ t, onDownloadCSV }: DocumentViewMenuListProps): MenuOption[] => {
	return [
		{
			title: t('downloadCSV'),
			onPress: onDownloadCSV,
			iconType: 'download',
		},
	];
};
